import { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  QrCode, Smartphone, Globe, Map as MapIcon, Cloud, Star, FileText,
  Bell, Shield, Zap, Check, ChevronRight, ArrowRight, Sparkles,
  BookOpen, ChevronDown
} from "lucide-react";
import { SEO_LANDINGS, SEO_LANDING_GROUPS, BASE_URL } from "../data/seoLandings";
import { WelcomeBookPhonePreview } from "../components/WelcomeBookPhonePreview";
import { useState } from "react";

const ICON_MAP = {
  QrCode, Smartphone, Globe, Map: MapIcon, Cloud, Star, FileText, Bell, Shield, Zap
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.05, ease: [0.25, 0.46, 0.45, 0.94] } })
};

const SeoGuestbookLanding = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const seoSlug = location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const config = SEO_LANDINGS[seoSlug];

  // Scroll to top on slug change
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [seoSlug]);

  const url = useMemo(() => `${BASE_URL}/${seoSlug}`, [seoSlug]);

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Pagina non trovata</h1>
          <p className="text-zinc-500 text-sm mb-4">Questa landing SEO non esiste.</p>
          <Link to="/" className="inline-block px-5 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-semibold">Torna alla home</Link>
        </div>
      </div>
    );
  }

  // Build FAQ schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": config.faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  // Product schema
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": config.productSchemaName,
    "description": config.description,
    "image": `${BASE_URL}/hostland-social.png`,
    "brand": { "@type": "Brand", "name": "Hostland by Vita da Host" },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": "9.90",
      "highPrice": "59.90",
      "offerCount": "2",
      "url": `${BASE_URL}/login`,
      "availability": "https://schema.org/InStock"
    }
  };

  // BreadcrumbList — hub-aware: le landing "Sito Web" puntano al pilastro
  // /sito-web-per-host, quelle Welcome Book a /guestbook-digitale.
  const hub = config.hub || { slug: "guestbook-digitale", label: "Welcome Book Digitale" };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": hub.label, "item": `${BASE_URL}/${hub.slug}` },
      { "@type": "ListItem", "position": 3, "name": config.h1, "item": url }
    ]
  };

  // HowTo schema (GEO-friendly) — only when the config declares steps.
  const howToSchema = config.howToSteps ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `Come creare un ${config.productSchemaName || "Welcome Book digitale"}`,
    "description": config.description,
    "step": config.howToSteps.map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.name,
      "text": s.text
    }))
  } : null;

  return (
    <div className="min-h-screen bg-white text-zinc-900" style={{ fontFamily: "system-ui, -apple-system, 'SF Pro Display', sans-serif" }}>
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.description} />
        <meta name="keywords" content={config.keywords} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={config.title} />
        <meta property="og:description" content={config.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${BASE_URL}/hostland-social.png`} />
        <meta property="og:locale" content="it_IT" />
        <meta property="og:site_name" content="Hostland" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.title} />
        <meta name="twitter:description" content={config.description} />
        <meta name="twitter:image" content={`${BASE_URL}/hostland-social.png`} />

        {/* Schema */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {howToSchema && <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>}
      </Helmet>

      {/* Top nav (minimal) */}
      <header className="border-b border-zinc-100 sticky top-0 z-30 bg-white/85 backdrop-blur-lg">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="text-base font-bold tracking-tight" data-testid="seo-nav-logo">Hostland</Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline-block text-sm font-semibold text-zinc-700 hover:text-zinc-900 px-3 py-1.5">Accedi</Link>
            <Link to="/login" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold text-white" style={{ background: "#FF385C" }} data-testid="seo-cta-nav">
              Prova gratis <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb visible */}
      <nav className="max-w-6xl mx-auto px-5 md:px-8 pt-5 md:pt-7" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1.5 text-xs text-zinc-500" data-testid="breadcrumbs">
          <li><Link to="/" className="hover:text-zinc-900">Home</Link></li>
          <li><ChevronRight className="w-3 h-3" /></li>
          <li><Link to={`/${hub.slug}`} className="hover:text-zinc-900">{hub.label}</Link></li>
          {seoSlug !== hub.slug && (
            <>
              <li><ChevronRight className="w-3 h-3" /></li>
              <li className="text-zinc-900 font-medium truncate max-w-[60vw]">{config.h1}</li>
            </>
          )}
        </ol>
      </nav>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-8 md:pt-16 pb-10 md:pb-20">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: "#FF385C" }} data-testid="seo-eyebrow">
            <Sparkles className="w-3.5 h-3.5 inline mr-1.5" />{config.eyebrow}
          </p>
        </motion.div>
        <motion.h1
          initial="hidden" animate="visible" variants={fadeUp} custom={1}
          className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] max-w-4xl"
          data-testid="seo-h1"
        >
          {config.hero.title}
        </motion.h1>
        <motion.p
          initial="hidden" animate="visible" variants={fadeUp} custom={2}
          className="mt-5 md:mt-7 text-base md:text-xl leading-relaxed max-w-3xl text-zinc-600"
          data-testid="seo-subtitle"
        >
          {config.hero.subtitle}
        </motion.p>
        <motion.div
          initial="hidden" animate="visible" variants={fadeUp} custom={3}
          className="mt-7 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
        >
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md"
            style={{ background: "#FF385C" }}
            data-testid="seo-hero-cta-primary"
          >
            {config.hero.ctaPrimary} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={config.ctaSecondaryLink || "/guestbook/prop_a00b4e59db32"}
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-base font-semibold border-2 border-zinc-200 hover:border-zinc-400 transition-colors"
            data-testid="seo-hero-cta-secondary"
          >
            <BookOpen className="w-4 h-4" /> {config.hero.ctaSecondary}
          </Link>
        </motion.div>
        <motion.p
          initial="hidden" animate="visible" variants={fadeUp} custom={4}
          className="mt-5 text-xs text-zinc-400"
        >
          Senza carta di credito • Disdici quando vuoi • Da 9,90€/mese
        </motion.p>
      </section>

      {/* ANSWER-FIRST DEFINITION (GEO) */}
      {config.definition && (
        <section className="max-w-4xl mx-auto px-5 md:px-8 pb-2 md:pb-4" data-testid="seo-definition">
          <div className="rounded-2xl border border-zinc-100 bg-zinc-50/70 p-5 md:p-7">
            <p className="text-base md:text-lg leading-relaxed text-zinc-700">
              {config.definitionTitle && <strong className="text-zinc-900">{config.definitionTitle} </strong>}
              {config.definition}
            </p>
          </div>
        </section>
      )}

      {/* LIVE WELCOME BOOK PREVIEW */}
      {config.demoIframeSrc && (
        <WelcomeBookPhonePreview
          iframeSrc={config.demoIframeSrc}
          heading={config.demoHeading}
          sub={config.demoSub}
          liveDemoLink={config.demoLiveLink}
          liveDemoLabel={config.demoLiveLabel}
        />
      )}

      {/* BENEFITS GRID */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-16" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)" }}>
        <motion.h2
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-2xl md:text-4xl font-bold tracking-tight max-w-2xl mb-8 md:mb-12"
        >
          {config.benefitsHeading || "Tutto quello che serve nel tuo Welcome Book, senza compromessi"}
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {config.benefits.map((b, i) => {
            const Icon = ICON_MAP[b.icon] || Sparkles;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="rounded-2xl p-5 md:p-6 bg-white border border-zinc-100 hover:border-zinc-300 hover:shadow-sm transition-all"
                data-testid={`seo-benefit-${i}`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: "#FFF1F4" }}>
                  <Icon className="w-5 h-5" style={{ color: "#FF385C" }} />
                </div>
                <h3 className="font-bold text-base mb-1.5">{b.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{b.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* LONG-FORM SECTIONS (SEO content) */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-16">
        {config.sections.map((sec, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            className="mb-8 md:mb-12"
            data-testid={`seo-section-${i}`}
          >
            <h2 className="text-xl md:text-3xl font-bold tracking-tight mb-3 md:mb-4">{sec.h2}</h2>
            <p className="text-base md:text-lg leading-relaxed text-zinc-600">{sec.body}</p>
          </motion.article>
        ))}
      </section>

      {/* CTA STRIP */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="rounded-3xl p-7 md:p-12 text-center" style={{ background: "linear-gradient(135deg, #FF385C 0%, #E61E4D 100%)" }}>
          <h2 className="text-2xl md:text-4xl font-bold text-white tracking-tight mb-3 md:mb-4">
            {config.ctaHeading || "Pronto a creare il tuo Welcome Book?"}
          </h2>
          <p className="text-white/90 text-sm md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto">
            {config.ctaSub || "Setup in 5 minuti. Provalo gratis 30 giorni, senza carta di credito."}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-7 md:px-9 py-3.5 md:py-4 rounded-full text-base md:text-lg font-bold bg-white text-zinc-900 hover:scale-[1.03] active:scale-[0.98] transition-all shadow-lg"
            data-testid="seo-cta-strip"
          >
            Inizia ora gratis <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <h2 className="text-2xl md:text-4xl font-bold tracking-tight mb-6 md:mb-10 text-center">Domande Frequenti</h2>
        <div className="space-y-2.5" data-testid="seo-faq-list">
          {config.faqs.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} idx={i} />
          ))}
        </div>
      </section>

      {/* INTERNAL LINKING — other landings */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-16 border-t border-zinc-100">
        <h2 className="text-xl md:text-3xl font-bold tracking-tight mb-6 md:mb-8 text-center">
          {config.linksHeading || "Esplora il Welcome Book Hostland per la tua tipologia"}
        </h2>
        {SEO_LANDING_GROUPS.map(group => (
          <div key={group.title} className="mb-8 md:mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3 md:mb-4 text-center">{group.title}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {group.items.map(it => {
                const active = it.slug === seoSlug;
                return (
                  <Link
                    key={it.slug}
                    to={`/${it.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      active ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200"
                    }`}
                    data-testid={`seo-link-${it.slug}`}
                  >
                    {it.label}
                    {!active && <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-100 py-8 md:py-10 mt-8">
        <div className="max-w-6xl mx-auto px-5 md:px-8 text-center">
          <p className="text-xs text-zinc-500">
            <span className="font-bold text-zinc-900">Hostland</span> by Vita da Host • P.IVA 02903680995 •{" "}
            <Link to="/privacy" className="hover:text-zinc-900">Privacy</Link> ·{" "}
            <Link to="/terms" className="hover:text-zinc-900">Termini</Link> ·{" "}
            <Link to="/cookie-policy" className="hover:text-zinc-900">Cookie</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="rounded-2xl border border-zinc-100 overflow-hidden" data-testid={`seo-faq-${idx}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
        data-testid={`seo-faq-toggle-${idx}`}
      >
        <span className="font-semibold text-sm md:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 -mt-1">
          <p className="text-sm md:text-base text-zinc-600 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
};

export default SeoGuestbookLanding;
